/**
 * Sentry init — client-side error tracking.
 *
 * We share a single Sentry project with another app. Events are
 * disambiguated by the `app` tag and the `bastubyggaren@<sha>` release
 * name, so alerts and dashboards can be filtered to this codebase.
 *
 * SSR (entry-server.tsx) does NOT call this — prerender errors should
 * fail the build, not flow to Sentry.
 */
import * as Sentry from '@sentry/react';
import { resolveClientMarket } from '../market/marketProfile';

let initialized = false;

// Config errors can be reported during module init, before initSentry() runs
// (imports evaluate before entry-client.tsx calls initSentry). Buffer them and flush
// once Sentry is up so a missing-backend misconfig still reaches Sentry.
const pendingConfigErrors: string[] = [];

export function initSentry(): void {
    if (initialized) return;

    const dsn = import.meta.env.VITE_SENTRY_DSN;
    if (!dsn) {
        // Not configured — skip silently so local dev and preview builds
        // without a DSN behave identically to production.
        return;
    }

    // Baked <html data-market> wins (same resolution App uses), so events are
    // tagged with the market the user actually saw rendered.
    const market = resolveClientMarket(import.meta.env.VITE_MARKET_DEFAULT);

    Sentry.init({
        dsn,
        environment: import.meta.env.MODE,
        release: import.meta.env.VITE_RELEASE_NAME ?? undefined,
        initialScope: {
            tags: {
                app: 'bastubyggaren',
                market,
            },
        },
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
    });

    initialized = true;

    for (const message of pendingConfigErrors.splice(0)) {
        Sentry.captureMessage(message, 'error');
    }
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Report a configuration error that degrades the app (e.g. missing backend
 * env) without throwing. Always logs to the console once so a misconfigured
 * deploy is visible in local/CI logs, and forwards to Sentry when a DSN is
 * configured so it can't pass unnoticed in production. Safe on SSR (Sentry is
 * only initialized on the client; captureMessage is a no-op otherwise).
 */
export function captureConfigError(message: string): void {
    console.error(`[config] ${message}`);
    if (initialized) {
        Sentry.captureMessage(message, 'error');
    } else {
        // Sentry not up yet (or has no DSN) — buffer for flush on init.
        pendingConfigErrors.push(message);
    }
}
